import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField, Button, Paper, Typography, Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, IconButton } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/common/Loader';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { Divider } from '@mui/material';

const maskEmail = (email) => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  return `${user.slice(0, 2)}****@${domain}`;
};

const isStrongPassword = (password) => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);
};

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regUsername, setRegUsername] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regErr, setRegErr] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [fpUsername, setFpUsername] = useState('');
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpStep, setFpStep] = useState(1);
  const [fpErr, setFpErr] = useState('');

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setErr('');
    try {
        const { data } = await axios.post('/api/auth/google-login', {
            credential: credentialResponse.credential
        });
        login(data.token, data.user);
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
    } catch (error) {
        setErr(error.response?.data?.message || 'Google Login Failed.');
    } finally {
        setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErr('Google Login was unsuccessful. Please try again.');
  };


  const submit = async e => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/login', { username, password });
      login(data.token, data.user);
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error) {
      setErr(error.response?.data?.message || 'Login Failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };
  
  const doRegister = async e => {
    e.preventDefault();
    if (!isStrongPassword(regPassword)) {
      setRegErr('Weak password. Use at least 8 chars, upper, lower, number, and special char.');
      return;
    }
    setRegErr('');
    setLoading(true);
    try {
      await axios.post('/api/auth/register', { username: regUsername, email: regEmail, password: regPassword });
      setShowRegister(false);
      setUsername(regUsername);
      setPassword('');
      setErr('Registration successful! Please login.');
    } catch (error) {
      setRegErr(error.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setFpErr('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/get-email-for-username', { username: fpUsername });
      setFpEmail(data.email);
      const otp = String(Math.floor(100000 + Math.random() * 900000));
      await axios.post('/api/auth/send-otp', { email: data.email, otp });
      setFpStep(2);
    } catch (error) {
      setFpErr(error.response?.data?.message || 'Failed to start password reset.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setFpErr('');
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { email: fpEmail, otp: fpOtp, newPassword: fpNewPassword });
      setForgotPasswordOpen(false);
      setFpStep(1);
      setErr('Password reset successful! Please login with your new password.');
    } catch (error) {
      setFpErr(error.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {loading && <Loader />}
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h5" align="center" gutterBottom>
          {showRegister ? 'Register' : 'Login'}
        </Typography>
        {err && <Alert severity="info" sx={{ mb: 2 }}>{err}</Alert>}
        
        {!showRegister ? (
          <form onSubmit={submit}>
            <TextField label="Username" value={username} onChange={e => setUsername(e.target.value)} fullWidth margin="normal" required />
            <TextField label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} fullWidth margin="normal" required InputProps={{
              endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>
            }} />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>Login</Button>
            <Divider sx={{ my: 2 }}>OR</Divider>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                />
            </Box>
            <Button onClick={() => setShowRegister(true)} fullWidth sx={{ mt: 1 }}>Don't have an account? Register</Button>
            <Button onClick={() => setForgotPasswordOpen(true)} fullWidth sx={{ mt: 1 }}>Forgot Password?</Button>
          </form>
        ) : (
          <form onSubmit={doRegister}>
            {regErr && <Alert severity="error" sx={{ mb: 2 }}>{regErr}</Alert>}
            <TextField label="Username" value={regUsername} onChange={e => setRegUsername(e.target.value)} fullWidth margin="normal" required />
            <TextField label="Email" type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} fullWidth margin="normal" required />
            <TextField label="Password" type={showPassword ? 'text' : 'password'} value={regPassword} onChange={e => setRegPassword(e.target.value)} fullWidth margin="normal" required InputProps={{
              endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>
            }} />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>Register</Button>
            <Button onClick={() => setShowRegister(false)} fullWidth sx={{ mt: 1 }}>Already have an account? Login</Button>
          </form>
        )}
      </Paper>

      <Dialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {fpErr && <Alert severity="error" sx={{ mb: 2 }}>{fpErr}</Alert>}
          {fpStep === 1 && (
            <TextField label="Enter your username" value={fpUsername} onChange={e => setFpUsername(e.target.value)} fullWidth margin="normal" />
          )}
          {fpStep === 2 && (
            <>
              <Typography>An OTP has been sent to {maskEmail(fpEmail)}.</Typography>
              <TextField label="Enter OTP" value={fpOtp} onChange={e => setFpOtp(e.target.value)} fullWidth margin="normal" />
              <TextField label="New Password" type="password" value={fpNewPassword} onChange={e => setFpNewPassword(e.target.value)} fullWidth margin="normal" />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotPasswordOpen(false)}>Cancel</Button>
          {fpStep === 1 && <Button onClick={handleForgotPassword}>Send OTP</Button>}
          {fpStep === 2 && <Button onClick={handleResetPassword}>Reset Password</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LoginPage;